import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { apiString as API_URL } from '../utils/constants';
import { AppThunk } from './store';
import { saveEngagementDraft } from './documentEngagement.slice';
import { copyObject } from '../utils/common';
import { SummaryNoteTypeEnum } from '../Enum/SummaryNoteTypeEnum';
import { gotoNextMenu, setAssessmentError } from './assessment.slice';
import { DevelopmentalHistoryModel } from '../Models/DevelopmentalHistory/DevelopmentalHistoryModel.model';
import { preparePriorAssessmentForNewDraft } from '../utils/aiFlagHelpers';

const developmentalHistorySlice = createSlice({
    name: 'developmentalHistorySlice',
    initialState: { developmentalHistoryInfo: new DevelopmentalHistoryModel(), isFetchingDevelopmentalHistoryInfo: false },
    reducers: {
        _setDevelopmentalHistoryInfo(state, action) {
            state.developmentalHistoryInfo = action.payload;
        },
        _setIsFetchingDevelopmentalHistoryInfo(state, action) {
            state.isFetchingDevelopmentalHistoryInfo = action.payload;
        },
    },
});

const { _setDevelopmentalHistoryInfo, _setIsFetchingDevelopmentalHistoryInfo } = developmentalHistorySlice.actions;

export const setDevelopmentalHistoryInfo =
    (info: DevelopmentalHistoryModel): AppThunk =>
    async (dispatch) => {
        dispatch(_setDevelopmentalHistoryInfo(info));
    };

export const setIsFetchingDevelopmentalHistoryInfo =
    (isFetching: boolean): AppThunk =>
    async (dispatch) => {
        dispatch(_setIsFetchingDevelopmentalHistoryInfo(isFetching));
    };

export const saveDevelopmentalHistoryInfo =
    (developmentalHistoryInfo: DevelopmentalHistoryModel, _saveLocal = false, gotoNext = false): AppThunk<Promise<boolean>> =>
    async (dispatch) => {
        const isUpdate = Boolean(developmentalHistoryInfo.Id);
        const url = isUpdate ? `${API_URL}/developmentalhistory/updatedevelopmentalhistory/` : `${API_URL}/developmentalhistory/insertdevelopmentalhistory/`;

        try {
            const response = await axios.put(url, developmentalHistoryInfo);
            const savedDevelopmentalHistory = isUpdate ? developmentalHistoryInfo : response.data;
            if (Boolean(response.data)) {
                dispatch(setDevelopmentalHistoryInfo(isUpdate ? developmentalHistoryInfo : { ...developmentalHistoryInfo, Id: response.data.Id }));
            }
            const isDraftSaved = await dispatch(prepareDevelopmentalHistoryNotes(savedDevelopmentalHistory));
            if (gotoNext && isDraftSaved) {
                dispatch(gotoNextMenu());
            }
            return isDraftSaved;
        } catch (err) {
            dispatch(setAssessmentError({ Show: true, Message: err?.response?.data }));
            return false;
        }
    };

const prepareDevelopmentalHistoryNotes =
    (developmentalHistory: DevelopmentalHistoryModel): AppThunk<Promise<boolean>> =>
    async (dispatch, getState) => {
        if (Boolean(developmentalHistory.Id)) {
            let engagementDraft = copyObject(getState().documentEngagement.engagementDraft);
            let memberAssessmentHistory = copyObject(getState().documentEngagement.memberAssessmentHistory || {});
            let summaryNotes = (engagementDraft.SummaryNotes || []).filter((s) => s.Type !== SummaryNoteTypeEnum.DevelopmentalHistory);
            summaryNotes.push({
                Id: developmentalHistory.MemberId,
                CptCode: '',
                Zcode: '',
                Order: 8,
                Note: 'Developmental History Completed',
                Type: SummaryNoteTypeEnum.DevelopmentalHistory,
                IsConfirm: true,
                DisplayName: 'Developmental History',
            });
            if (developmentalHistory.ContextualNonpersonalFactors === 'Divorce in the family') {
                summaryNotes.push({
                    Id: developmentalHistory.MemberId,
                    CptCode: 'T1016',
                    Zcode: 'Z63.5',
                    Order: 9,
                    Note: 'Disruption of family by separation and divorce',
                    Type: SummaryNoteTypeEnum.DevelopmentalHistory,
                    IsConfirm: true,
                    DisplayName: 'Developmental History',
                });
            }
            if (developmentalHistory.ContextualNonpersonalFactors === 'A recent death') {
                summaryNotes.push({
                    Id: developmentalHistory.MemberId,
                    CptCode: 'T1016',
                    Zcode: 'Z63.4',
                    Order: 9,
                    Note: 'Disappearance and death of family member',
                    Type: SummaryNoteTypeEnum.DevelopmentalHistory,
                    IsConfirm: true,
                    DisplayName: 'Developmental History',
                });
            }
            if (developmentalHistory.ContextualNonpersonalFactors === 'Natural disaster') {
                summaryNotes.push({
                    Id: developmentalHistory.MemberId,
                    CptCode: 'T1016',
                    Zcode: 'Z58.89',
                    Order: 9,
                    Note: 'Other problems related to physical environment',
                    Type: SummaryNoteTypeEnum.DevelopmentalHistory,
                    IsConfirm: true,
                    DisplayName: 'Developmental History',
                });
            }
            if (developmentalHistory.ContextualNonpersonalFactors === 'Economic downturn') {
                summaryNotes.push({
                    Id: developmentalHistory.MemberId,
                    CptCode: 'T1016',
                    Zcode: 'Z59.86',
                    Order: 9,
                    Note: 'Financial insecurity',
                    Type: SummaryNoteTypeEnum.DevelopmentalHistory,
                    IsConfirm: true,
                    DisplayName: 'Developmental History',
                });
            }

            engagementDraft.SummaryNotes = summaryNotes;
            const updatedMemberAssessmentStatuses = {
                ...memberAssessmentHistory.MemberAssessmentStatuses,
                DevelopmentalHistoryStatus: developmentalHistory.AssessmentStatus,
            };
            const updatedEngagementDraft = {
                ...engagementDraft,
                DevelopmentalHistoryId: developmentalHistory.Id,
            };
            const updatedMemberAssessmentHistory = {
                ...memberAssessmentHistory,
                MemberAssessmentStatuses: updatedMemberAssessmentStatuses,
                EngagementDraft: updatedEngagementDraft,
            };
            return dispatch(saveEngagementDraft(updatedMemberAssessmentHistory));
        }
        return false;
    };

export const fetchDevelopmentalHistoryInfo =
    (id: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsFetchingDevelopmentalHistoryInfo(true));
        const response = await axios.get(`${API_URL}/developmentalhistory/getdevelopmentalhistoryinfobyid?id=${id}`);
        dispatch(setDevelopmentalHistoryInfo(response.data));
        dispatch(setIsFetchingDevelopmentalHistoryInfo(false));
    };

export const fetchDevelopmentalHistoryInfoByMemberId =
    (memberId: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsFetchingDevelopmentalHistoryInfo(true));
        const response = await axios.get(`${API_URL}/developmentalhistory/getdevelopmentalhistoryinfobymemberid?memberId=${memberId}`);
        if (response.data?.Id) {
            preparePriorAssessmentForNewDraft(response.data);
            dispatch(setDevelopmentalHistoryInfo(response.data));
        } else {
            dispatch(setDevelopmentalHistoryInfo(new DevelopmentalHistoryModel()));
        }
        dispatch(setIsFetchingDevelopmentalHistoryInfo(false));
    };

export default developmentalHistorySlice.reducer;
